import { useEffect, useState } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, UserPlus, Link2, AlertTriangle } from "lucide-react";

// Prospect quote (client is not a Sendtoo customer yet): show customers that look like the same client and let staff
// link one, or create the customer once from the quote. Prevents a 2nd customer being created at booking time.
export default function QuoteCustomerLink({ quote, onLinked }) {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/v2/quotations/${quote.id}/customer-match`).then((r) => setM(r.data.matches || [])).catch(() => setM([]));
  }, [quote.id]);
  const link = async (cid) => {
    setBusy(true);
    try { const r = await api.post(`/v2/quotations/${quote.id}/link-customer`, { customer_id: cid }); toast.success("Linked to the customer"); onLinked(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not link"); }
    finally { setBusy(false); }
  };
  const create = async (confirm = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/v2/quotations/${quote.id}/create-customer`, { confirm_duplicate: confirm });
      toast.success(r.data.customer_created ? "Customer created and linked" : (r.data.customer_message || "Linked to the existing customer"));
      onLinked(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.matches) {
        setM(d.matches.map((x) => ({ ...x, reasons: x.reasons || [] })));
        if (window.confirm(`${d.message}. Link to one of them instead (Cancel), or press OK to create a separate customer anyway?`)) await create(true);
      } else toast.error(typeof d === "string" ? d : "Could not create the customer");
    } finally { setBusy(false); }
  };
  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="quote-customer-link">
      <p className="font-semibold text-amber-900 inline-flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{quote.customer_snapshot?.name || "This client"} is not linked to a Sendtoo customer yet</p>
      <p className="text-xs text-amber-800 mt-0.5">Link the existing customer, or create the customer once from this quote, so the same client is not created again at booking.</p>
      {m === null ? <Loader2 className="w-4 h-4 animate-spin mt-2 text-amber-700" /> : m.length > 0 && (
        <ul className="mt-2 space-y-1">{m.map((x) => (
          <li key={x.id} className="flex flex-wrap items-center gap-2 bg-white rounded-md border border-amber-200 px-3 py-1.5">
            <span className="font-semibold text-slate-800">{x.company || x.name}</span>
            <span className="text-xs text-slate-500">{x.customer_code} · {x.mobile} {x.email ? `· ${x.email}` : ""}</span>
            <span className="text-[11px] text-amber-800">same {x.reasons.join(", ")}</span>
            <button disabled={busy} onClick={() => link(x.id)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline disabled:opacity-50"><Link2 className="w-3 h-3" />Link</button>
          </li>))}</ul>)}
      <button disabled={busy} onClick={() => create(false)} data-testid="create-customer-from-quote"
        className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}Create customer from this quote</button>
    </div>
  );
}
